import { AppState, HabitType } from '@/types';
import { calculateHabiticaReward, shouldResetDailyCompletions, getHabiticaColorState, HABITICA_VALUE_CONFIG, calculateDailyMissPenalty, calculateNegativeHabitPenalty, getConstitutionModifier } from '@/lib/habitValueSystem';
import { saveHabitToSupabase, saveDailyToSupabase, saveTodoToSupabase, markDailyCompleteInSupabase, markDailyUncompleteInSupabase, markTodoCompleteInSupabase, markTodoUncompleteInSupabase, logHabitCompletionToSupabase, deleteHabitFromSupabase, deleteDailyFromSupabase, deleteTodoFromSupabase } from '@/utils/supabaseHabitSaver';

export interface HabiticaActions {
  createHabit: (habit: Omit<HabitType, 'id' | 'streak' | 'completedToday' | 'lastCompleted'>) => Promise<void>;
  updateHabit: (habitId: string, habit: Partial<HabitType>) => Promise<void>;
  completeHabit: (habitId: string) => Promise<void>;
  uncompleteHabit: (habitId: string) => Promise<void>;
  deleteHabit: (habitId: string) => Promise<void>;
  clickNegativeHabit: (habitId: string) => void;
  takeDamage: (amount: number, reason: string) => void;
  applyDailyDamage: () => void;
}

export const createHabiticaActions = (
  state: AppState,
  dispatch: React.Dispatch<any>
): HabiticaActions => {
  return {
    createHabit: async (habitData) => {
      if (import.meta.env.DEV) console.log('🔥 createHabit action called with data:', habitData);
      
      const habit: HabitType = {
        ...habitData,
        id: crypto.randomUUID(),
        userId: state.user?.id || '',
        streak: 0,
        completedToday: false,
        lastCompleted: undefined,
        habitValue: HABITICA_VALUE_CONFIG.DEFAULT_VALUE,
        dailyCompletions: 0,
        colorState: 'yellow',
        createdAt: new Date(),
        archived: false
      };

      dispatch({
        type: 'CREATE_HABIT',
        payload: { habit }
      });

      // Save to Supabase in background
      try {
        if (habit.type === 'habit' || habit.type === 'positive' || habit.type === 'negative') {
          await saveHabitToSupabase(habit);
        } else if (habit.type === 'daily') {
          await saveDailyToSupabase(habit);
        } else if (habit.type === 'todo') {
          await saveTodoToSupabase(habit);
        }
      } catch (error) {
        if (import.meta.env.DEV) console.error('❌ Error saving habit to Supabase (but kept in local state):', error);
      }
    },

    updateHabit: async (habitId, habitData) => {
      if (import.meta.env.DEV) console.log('🔄 updateHabit action called:', { habitId, habitData });
      const existingHabit = state.habits.find(h => h.id === habitId);
      if (!existingHabit) {
        if (import.meta.env.DEV) console.log('❌ updateHabit - Habit not found:', habitId);
        return;
      }

      const updatedHabit = {
        ...habitData,
        id: habitId,
        userId: existingHabit.userId,
        streak: existingHabit.streak,
        completedToday: existingHabit.completedToday,
        lastCompleted: existingHabit.lastCompleted,
        habitValue: existingHabit.habitValue,
        dailyCompletions: existingHabit.dailyCompletions,
        colorState: existingHabit.colorState
      };

      dispatch({
        type: 'UPDATE_HABIT',
        payload: { 
          habitId, 
          updatedHabit
        }
      });

      // Save to Supabase
      if (existingHabit.type === 'habit' || existingHabit.type === 'positive' || existingHabit.type === 'negative') {
        await saveHabitToSupabase(updatedHabit as HabitType);
      } else if (existingHabit.type === 'daily') {
        await saveDailyToSupabase(updatedHabit as HabitType);
      } else if (existingHabit.type === 'todo') {
        await saveTodoToSupabase(updatedHabit as HabitType);
      }
    },

    completeHabit: async (habitId) => {
      const habit = state.habits.find(h => h.id === habitId);
      if (!habit) {
        if (import.meta.env.DEV) console.error('❌ Habit not found for completion:', habitId);
        return;
      }

      // For dailies, prevent multiple completions
      if (habit.type === 'daily' && habit.completedToday) {
        if (import.meta.env.DEV) console.log('⚠️ Daily already completed today, skipping');
        return;
      }

      dispatch({
        type: 'COMPLETE_HABIT',
        payload: { habitId }
      });

      // Save completion to Supabase
      try {
        if (habit.type === 'daily') {
          await markDailyCompleteInSupabase(habitId);
        } else if (habit.type === 'todo') {
          await markTodoCompleteInSupabase(habitId);
        } else if (habit.type === 'habit' || habit.type === 'positive' || habit.type === 'negative') {
          await logHabitCompletionToSupabase(habitId, habit.userId, 1, habit.points || 1);
        }
      } catch (error) {
        if (import.meta.env.DEV) console.error('❌ Error saving completion to Supabase:', error);
      }
    },

    uncompleteHabit: async (habitId) => {
      const habit = state.habits.find(h => h.id === habitId);
      if (!habit) {
        if (import.meta.env.DEV) console.error('❌ Habit not found for uncomplete:', habitId);
        return;
      }

      // Only allow uncompleting if the habit was actually completed
      if (habit.type === 'habit' && habit.dailyCompletions === 0) {
        if (import.meta.env.DEV) console.log('⚠️ Habit has no completions to undo');
        return;
      }
      if (habit.type === 'daily' && !habit.completedToday) {
        return;
      }
      if (habit.type === 'todo' && !habit.completedToday) {
        return;
      }

      dispatch({
        type: 'UNCOMPLETE_HABIT',
        payload: { habitId }
      });

      // Save uncomplete to Supabase
      try {
        if (habit.type === 'habit' || habit.type === 'positive' || habit.type === 'negative') {
          await logHabitCompletionToSupabase(habitId, habit.userId, -1, -(habit.points || 1));
        } else if (habit.type === 'daily') {
          await markDailyUncompleteInSupabase(habitId);
        } else if (habit.type === 'todo') {
          await markTodoUncompleteInSupabase(habitId);
        }
      } catch (error) {
        if (import.meta.env.DEV) console.error('❌ Error saving uncomplete to Supabase:', error);
      }
    },

    deleteHabit: async (habitId) => {
      if (import.meta.env.DEV) console.log('🗑️ Delete habit action called:', habitId);
      const habit = state.habits.find(h => h.id === habitId);
      
      dispatch({
        type: 'DELETE_HABIT',
        payload: { habitId }
      });

      // Delete from Supabase
      if (habit) {
        if (habit.type === 'habit' || habit.type === 'positive' || habit.type === 'negative') {
          await deleteHabitFromSupabase(habitId);
        } else if (habit.type === 'daily') {
          await deleteDailyFromSupabase(habitId);
        } else if (habit.type === 'todo') {
          await deleteTodoFromSupabase(habitId);
        }
      }
    },

    clickNegativeHabit: (habitId) => {
      const habit = state.habits.find(h => h.id === habitId);
      if (!habit || habit.type !== 'habit') return;

      const constitution = 10;
      
      dispatch({
        type: 'CLICK_NEGATIVE_HABIT',
        payload: { habitId, constitution }
      });
    },

    takeDamage: (amount, reason) => {
      dispatch({
        type: 'TAKE_DAMAGE',
        payload: { amount, reason, timestamp: new Date() }
      });
    },

    applyDailyDamage: () => {
      const now = new Date();
      const lastCheck = new Date(state.lastDamageCheck);
      const daysSinceCheck = Math.floor((now.getTime() - lastCheck.getTime()) / (1000 * 60 * 60 * 24));

      if (daysSinceCheck > 0) {
        const incompleteDailies = state.habits.filter(h => 
          h.type === 'daily' && 
          !h.completedToday
        );

        if (incompleteDailies.length > 0) {
          const constitution = 10;
          
          dispatch({
            type: 'APPLY_DAILY_DAMAGE',
            payload: { 
              incompleteDailies,
              constitution,
              newDamageCheck: now
            }
          });
        }
      }
    }
  };
};
